import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tr } from '@/lib/i18n-core';
import { post, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { Button, Card, CardContent, CardHeader, CardTitle, DatePicker, Field, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import type { Encounter } from './types';

export function FollowUpDate({ encounter }: { encounter: Encounter }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [date, setDate] = useState(encounter.followUpDate ? encounter.followUpDate.slice(0, 10) : '');

  const save = useMutation({
    mutationFn: (next: string) => put(`/encounters/${encounter.id}`, { followUpDate: next ? `${next}T10:00:00.000Z` : null }),
    onSuccess: () => {
      toast.success(date ? tr('opd', 'follow_up_date_saved') : tr('opd', 'follow_up_cleared'), date ? tr('opd', 'reception_sees_it_in_follow_ups_due_and_reminds') : undefined);
      void queryClient.invalidateQueries({ queryKey: ['encounter', encounter.id] });
    },
    onError: (error) => toast.error(tr('opd', 'the_follow_up_date_could_not_be_saved'), describeError(error)),
  });

  return (
    <Card className="mt-4">
      <CardContent className="flex flex-wrap items-end gap-3 pt-4">
        <Field label={tr('opd', 'ask_the_patient_to_come_back_on')} className="w-56">
          <DatePicker min={new Date().toISOString().slice(0, 10)} value={date} onChange={setDate} />
        </Field>
        <Button variant="outline" onClick={() => save.mutate(date)} loading={save.isPending}>
          {date ? tr('opd', 'save_follow_up_date') : tr('opd', 'no_follow_up_needed')}
        </Button>
      </CardContent>
    </Card>
  );
}

export function Addenda({ encounter }: { encounter: Encounter }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');

  const add = useMutation({
    mutationFn: () => post(`/encounters/${encounter.id}/addenda`, { body }),
    onSuccess: () => {
      setBody('');
      toast.success(tr('opd', 'addendum_added'), tr('opd', 'it_is_signed_with_your_name_and_the_time_and'));
      void queryClient.invalidateQueries({ queryKey: ['encounter', encounter.id] });
    },
    onError: (error) => toast.error(tr('opd', 'the_addendum_could_not_be_added'), describeError(error)),
  });

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm">{tr('opd', 'addenda')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {encounter.addenda.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {tr('opd', 'the_note_is_closed_anything_that_must_be_added')}
          </p>
        )}
        {encounter.addenda.map((addendum) => (
          <div key={addendum.id} className="rounded-md border p-3 text-sm">
            <p className="whitespace-pre-wrap">{addendum.body}</p>
            <p className="mt-2 text-xs text-muted-foreground">
              {addendum.authorName ?? tr('opd', 'unknown')} · {dateTime(addendum.createdAt)}
            </p>
          </div>
        ))}
        {can('encounter.write') && (
          <>
            <Field label={tr('opd', 'new_addendum')}>
              <Textarea rows={3} value={body} onChange={(event) => setBody(event.target.value)} />
            </Field>
            <Button onClick={() => add.mutate()} loading={add.isPending} disabled={body.trim().length < 5}>
              {tr('opd', 'add_addendum')}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
